import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Chip, Grid2, Skeleton, Stack, Typography } from '@mui/material';

import { colors } from 'src/global/styles/colors';
import { useProjectDashboardContext } from 'src/hooks/useProjectDashboardContext';
import { useGetProjectOverviewQuery } from 'src/store/services/project-dashboard.service';

interface KPICardProps {
  title: string;
  value: string;
  change?: string;
  changeType?: 'positive' | 'negative';
  badge?: string;
  badgeColor?: string;
}

const KPICard = ({ title, value, change, changeType, badge, badgeColor }: KPICardProps) => (
  <Box
    sx={{
      background: colors.neutral03,
      backdropFilter: 'blur(100px)',
      borderRadius: '8px',
      padding: '24px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}>
    <Typography
      sx={{
        color: colors.neutral90,
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
      }}>
      {title}
    </Typography>

    <Stack spacing={1}>
      <Stack direction='row' alignItems='center' spacing={2}>
        <Typography
          sx={{
            color: colors.white,
            fontSize: '26px',
            fontWeight: 700,
            lineHeight: '31px',
          }}>
          {value}
        </Typography>
        {badge && (
          <Chip
            label={badge}
            size='small'
            sx={{
              backgroundColor: badgeColor,
              color: colors.white,
              fontSize: '12px',
              fontWeight: 500,
              height: '24px',
              borderRadius: '4px',
            }}
          />
        )}
      </Stack>

      {change && (
        <Stack direction='row' alignItems='center' spacing={1}>
          <TrendingUpIcon
            sx={{
              color: changeType === 'positive' ? colors.green300 : colors.red300,
              fontSize: '12px',
            }}
          />
          <Typography
            sx={{
              color: changeType === 'positive' ? colors.green300 : colors.red300,
              fontSize: '12px',
              fontWeight: 400,
            }}>
            {change}
          </Typography>
          <Typography
            sx={{
              color: colors.neutral90,
              fontSize: '12px',
              fontWeight: 400,
            }}>
            vs last week
          </Typography>
        </Stack>
      )}
    </Stack>
  </Box>
);

const KPICards = () => {
  const { projectId, address, network } = useProjectDashboardContext();
  const {
    data: overviewData,
    isLoading,
    error,
  } = useGetProjectOverviewQuery({ projectId, address, network }, { skip: !projectId || !address || !network });

  if (isLoading) {
    return (
      <Grid2 container spacing={3}>
        {[...Array(4)].map((_, index) => (
          <Grid2 key={index} size={{ xs: 12, sm: 6, lg: 3 }}>
            <Box
              sx={{
                background: colors.neutral03,
                backdropFilter: 'blur(100px)',
                borderRadius: '8px',
                padding: '24px',
                height: '100%',
              }}>
              <Skeleton variant='text' width='60%' height={20} sx={{ mb: 2 }} />
              <Skeleton variant='text' width='40%' height={32} sx={{ mb: 1 }} />
              <Skeleton variant='text' width='30%' height={16} />
            </Box>
          </Grid2>
        ))}
      </Grid2>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Typography sx={{ color: colors.red200, textAlign: 'center' }}>Failed to load project overview data</Typography>
      </Box>
    );
  }

  return (
    <Grid2 container spacing={3}>
      <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
        <KPICard
          title='Total Nodes Sold'
          value={overviewData?.totalNodeSold?.toLocaleString() ?? '0'}
          change={overviewData?.totalNodeSoldLastWeek ? `+${overviewData.totalNodeSoldLastWeek} nodes` : undefined}
          changeType='positive'
          badge={overviewData ? `Tier ${overviewData.currentTier}/${overviewData.maximumTier}` : undefined}
          badgeColor={colors.red300}
        />
      </Grid2>

      <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
        <KPICard
          title='Funding Status'
          value={overviewData?.fundingStatus ?? 'Unknown'}
          badge={overviewData ? `Phase ${overviewData.fundingPhases}` : undefined}
          badgeColor='#495DB6'
        />
      </Grid2>

      <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
        <KPICard
          title='LFG Funding Revenue'
          value={overviewData?.LFGFundingRevenue?.toLocaleString() ?? '0'}
          change={
            overviewData?.percentLFGFundingLastWeek
              ? `${overviewData.percentLFGFundingLastWeek?.toFixed(2)}%`
              : undefined
          }
          changeType={overviewData?.percentLFGFundingLastWeek ? 'positive' : 'negative'}
        />
      </Grid2>

      <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
        <KPICard
          title='Project Funding Revenue'
          value={overviewData?.projectFundingRevenue?.toLocaleString() ?? '0'}
          change={
            overviewData?.percentProjectFundingRevenue
              ? `${overviewData.percentProjectFundingRevenue?.toFixed(2)}%`
              : undefined
          }
          changeType={overviewData?.percentProjectFundingRevenue ? 'positive' : 'negative'}
        />
      </Grid2>
    </Grid2>
  );
};

export default KPICards;
